import type { ServerMessage } from '@shared/protocol';
import type { GameEvent } from '@shared/game/types';

export interface BoardPresentationQueue {
  subscribe(handler: (msg: ServerMessage) => void): () => void;
  push(msg: ServerMessage): void;
  flush(): void;
  clear(): void;
  flushDroppedKeyPickups(playerId: string): void;
  flushPassThroughCollections(playerId: string, nodeId?: string): void;
  dispose(): void;
}

interface BoardPresentationQueueOptions {
  isBlocked(): boolean;
}

interface PresentationSubscriber {
  handler: (msg: ServerMessage) => void;
  deferred: ServerMessage[];
}

const LANDING_ECONOMY_REASONS = new Set([
  'gold_tile',
  'dropped_keys_pickup',
  'damage_drop',
  'death_drop',
  'trophy_purchase',
  'soul_anchor_steal',
]);

const LANDING_DAMAGE_SOURCES = new Set(['hazard_tile', 'soul_anchor', 'item_toxic_cloud']);
const LANDING_HEAL_SOURCES = new Set(['heal_tile']);

export function createBoardPresentationQueue(
  options: BoardPresentationQueueOptions,
): BoardPresentationQueue {
  const subscribers = new Set<PresentationSubscriber>();
  let disposed = false;
  let flushing = false;

  function push(msg: ServerMessage): void {
    if (disposed) return;
    const defer = shouldDeferBoardPresentationMessage(msg, options.isBlocked());
    for (const subscriber of subscribers) {
      if (defer) {
        subscriber.deferred.push(msg);
      } else {
        subscriber.handler(msg);
      }
    }
  }

  function subscribe(handler: (msg: ServerMessage) => void): () => void {
    if (disposed) return () => {};
    const subscriber: PresentationSubscriber = { handler, deferred: [] };
    subscribers.add(subscriber);
    return () => {
      subscribers.delete(subscriber);
      subscriber.deferred.length = 0;
    };
  }

  function flush(): void {
    if (disposed || flushing || options.isBlocked()) return;
    flushing = true;
    try {
      for (const subscriber of subscribers) {
        while (subscriber.deferred.length > 0 && !options.isBlocked()) {
          const msg = subscriber.deferred.shift()!;
          subscriber.handler(msg);
        }
      }
    } finally {
      flushing = false;
    }
  }

  function flushDroppedKeyPickups(playerId: string): void {
    flushSelected(() => (msg) => isDroppedKeyPickupForPlayer(msg, playerId));
  }

  function flushPassThroughCollections(playerId: string, nodeId?: string): void {
    flushSelected(() => {
      const pendingDroppedKeyEconomyPlayers: string[] = [];
      return (msg) => {
        const collectionPlayerId = getDroppedKeyCollectionPlayerId(msg, playerId, nodeId);
        if (collectionPlayerId != null) {
          pendingDroppedKeyEconomyPlayers.push(collectionPlayerId);
          return true;
        }
        const matchingIndex = pendingDroppedKeyEconomyPlayers.findIndex((pendingPlayerId) =>
          isDroppedKeyPickupForPlayer(msg, pendingPlayerId),
        );
        if (matchingIndex >= 0) {
          pendingDroppedKeyEconomyPlayers.splice(matchingIndex, 1);
          return true;
        }
        return false;
      };
    });
  }

  function flushSelected(createSelector: () => (msg: ServerMessage) => boolean): void {
    if (disposed || flushing) return;
    flushing = true;
    try {
      for (const subscriber of subscribers) {
        const shouldFlush = createSelector();
        const retained: ServerMessage[] = [];
        const selected: ServerMessage[] = [];
        for (const msg of subscriber.deferred) {
          if (shouldFlush(msg)) {
            selected.push(msg);
          } else {
            retained.push(msg);
          }
        }
        subscriber.deferred = retained;
        for (const msg of selected) {
          subscriber.handler(msg);
        }
      }
    } finally {
      flushing = false;
    }
  }

  function clear(): void {
    for (const subscriber of subscribers) {
      subscriber.deferred.length = 0;
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    clear();
    subscribers.clear();
  }

  return {
    subscribe,
    push,
    flush,
    clear,
    flushDroppedKeyPickups,
    flushPassThroughCollections,
    dispose,
  };
}

export function shouldDeferBoardPresentationMessage(
  msg: ServerMessage,
  isMovementBusy: boolean,
): boolean {
  if (!isMovementBusy) return false;
  if (msg.type === 'state_sync') return true;
  if (msg.type !== 'event') return false;
  return isLandingPresentationEvent(msg.event);
}

function isLandingPresentationEvent(event: GameEvent): boolean {
  switch (event.type) {
    case 'landing':
    case 'board_object_collected':
    case 'player_received_item':
    case 'soul_anchor_claim':
    case 'soul_anchor_trigger':
    case 'respawn':
      return true;
    case 'economy_change':
      return LANDING_ECONOMY_REASONS.has(event.reason);
    case 'damage':
      return LANDING_DAMAGE_SOURCES.has(event.source);
    case 'heal':
      return LANDING_HEAL_SOURCES.has(event.source);
    case 'death':
      return LANDING_DAMAGE_SOURCES.has(event.cause);
    case 'item_blocked_stoneskin':
      return event.itemId === 'hazard_tile' || event.itemId === 'toxic_cloud';
    default:
      return false;
  }
}

function getDroppedKeyCollectionPlayerId(
  msg: ServerMessage,
  playerId: string,
  nodeId?: string,
): string | null {
  if (msg.type !== 'event') return null;
  const event = msg.event;
  if (
    event.type === 'board_object_collected' &&
    (nodeId == null ? event.playerId === playerId : event.nodeId === nodeId) &&
    event.objectKind === 'dropped_keys' &&
    event.quantity > 0
  ) {
    return event.playerId;
  }
  return null;
}

function isDroppedKeyPickupForPlayer(msg: ServerMessage, playerId: string): boolean {
  if (msg.type !== 'event') return false;
  const event = msg.event;
  return (
    event.type === 'economy_change' &&
    event.playerId === playerId &&
    event.resource === 'keys' &&
    event.reason === 'dropped_keys_pickup' &&
    event.newValue > event.oldValue
  );
}
